// =====================================================================
// ngay.ts — helper ngày/giờ dịch từ Data.gs của ClassFlow (parseVNDate_, dateKey_, timeKey_,
// startOfDay_, addDays_, weekdayLabel_, formatVNDate_).
//
// Biểu diễn 1 thời điểm = "ms giờ tường": số ms tính như thể giờ Việt Nam là UTC
// (Date.UTC(y, m-1, d, h, mi)). Vì sao: GAS chạy Date theo múi Asia/Ho_Chi_Minh (+7, không đổi giờ
// mùa hè) nên mọi phép so/cộng ngày của Engine.gs tương đương phép tính trên giờ tường. Làm vậy thì
// kết quả KHÔNG phụ thuộc múi giờ của máy chạy (trình duyệt ở đâu cũng ra đúng ngày VN), và không bao
// giờ phải new Date('dd/MM/yyyy').
// =====================================================================

export const MOT_NGAY = 86_400_000;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** 'YYYY-MM-DD' (dạng lưu trong DB) ⇒ ms giờ tường lúc 00:00. Rỗng/sai ⇒ null. */
export function tuIso(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** ms giờ tường ⇒ 'YYYY-MM-DD' (= dateKey_ của GAS). */
export function ngayIso(t: number): string {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
export const dateKey = ngayIso;

/** ms giờ tường ⇒ 'dd/MM/yyyy' (= formatVNDate_) — chỉ dùng cho câu cảnh báo, giữ y chữ GAS. */
export function formatVN(t: number): string {
  const d = new Date(t);
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/**
 * parseVNDate_ — CHỈ nhận 'd/M/yyyy' hoặc 'd/M/yyyy H:mm'. Dạng 'yyyy-mm-dd' ⇒ null (GAS bỏ qua dòng
 * đó — đúng nguồn gốc lệch NL-002). Tràn ngày tự cộng như new Date(y, m-1, d) của GAS (31/02 ⇒ 03/03).
 * Dùng cho các chuỗi ngày nằm trong `Tham số` của DIEU_CHINH (P1 nạp nguyên JSON, không chuẩn hoá).
 */
export function parseVNDate(v: unknown): number | null {
  if (!v) return null;
  const s = String(v).trim();
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/.exec(s);
  if (!m) return null;
  const t = Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4] || 0), Number(m[5] || 0), 0, 0);
  return Number.isNaN(t) ? null : t;
}

/** startOfDay_ */
export function startOfDay(t: number): number {
  return t - (((t % MOT_NGAY) + MOT_NGAY) % MOT_NGAY);
}

/** addDays_ (giờ VN không có giờ mùa hè ⇒ cộng đúng n × 24h) */
export function addDays(t: number, n: number): number {
  return t + n * MOT_NGAY;
}

/** Thứ ISO của ngày: 1 = Thứ 2 … 7 = CN (GAS dùng nhãn '2'..'7','CN' — cùng nghĩa). */
export function thuIso(t: number): number {
  const g = new Date(t).getUTCDay(); // 0 = CN
  return g === 0 ? 7 : g;
}

/**
 * timeKey_ — chuẩn hoá giờ về 'HH:mm' để so ca. Nhận '8:00' / '08:00' / '08:00:00' (Postgres trả time
 * kèm giây). Không đọc được ⇒ ''.
 */
export function timeKey(v: unknown): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v ?? ""));
  return m ? `${pad2(Number(m[1]))}:${m[2]}` : "";
}

/** 'Hôm nay' theo giờ Việt Nam (UTC+7, không đổi giờ mùa hè) ⇒ 'YYYY-MM-DD'. `bayGio` = Date.now(). */
export function homNayVN(bayGio: number): string {
  return ngayIso(startOfDay(bayGio + 7 * 3_600_000));
}

/** 'YYYY-MM-DD' hoặc 'YYYY-MM-DD HH:mm' ⇒ 'dd/MM/yyyy[ HH:mm]' — chỉ để HIỂN THỊ (đúng dạng Tab 1 ClassFlow). */
export function isoSangVN(s: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}))?/.exec(s ?? "");
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}` + (m[4] ? " " + m[4] : "");
}
